import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, OnInit } from '@angular/core';
import { NextPage } from '@cvx/nextpage';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { finalize } from 'rxjs';
import { PrimengModule } from '../../../modules/primeng/primeng.module';
import { HomeService } from '../../../services/home.service';
import { CommonService } from '../../../services/common.service';
import { environment } from '../../../../environments/environment';

@Component({
    selector: 'app-home-locations',
    imports: [NextPage, CommonModule, PrimengModule, RouterModule],
    templateUrl: './home-locations.component.html',
    styleUrl: './home-locations.component.scss',
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class HomeLocationsComponent implements OnInit {

  locations: any[] = [];
  portalDetails: any;
  loading = true; // Initialize as true to show loader immediately

  // Static facility codes - these will always be used regardless of API response
  private readonly facilityCodes = ['ES', 'PA', 'PN', 'SL'];

  // Static location name map
  private readonly nameMap: Record<string, string> = {
    'ES': 'EI Segundo',
    'PA': 'Pascagoula',
    'PN': 'Pasadena',
    'SL': 'Salt Lake'
  };
  facilityCode: any;

  constructor(private router: Router, private homeService: HomeService, private commonService: CommonService) {
    // Check for state passed during navigation (for handling new tab cases)
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      const state = this.router.getCurrentNavigation()?.extras.state;
      if (state && state['facilityCode']) {
        localStorage.setItem('current_facility_id', state['facilityCode']);
        this.commonService.updateCurrentFacilityId(this.facilityCode);

      }
    });
  }

  ngOnInit() {
    console.log('Current environment:', environment);
    this.homeService.getListData()
      .pipe(finalize(() => this.loading = false))
      .subscribe({
        next: (data: any) => {
          // If API returns no data, create static data with placeholder values
          if (!data || data.length === 0) {
            this.createStaticLocationData();
          } else {
            this.processApiLocationData(data);
          }
        },
        error: () => {
          // In case of error, create static data
          this.createStaticLocationData();
        }
      });
  }

  /**
   * Processes location data returned from the API
   */
  private processApiLocationData(data: any[]) {
    this.locations = data.map((item: any) => {
      const code = item.FacilityID || '';

      // Use the static name map for consistency
      const locationName = this.nameMap[code] || code;

      // Use facility code for descriptions
      const descName = code;

      // Use different image for each location based on facility code
      const imagePath = this.getLocationImagePath(code);

      return {
        name: locationName,
        route: '/details',
        image: imagePath, // Dynamic image path based on location
        disabled: code === 'SL', // Mark Salt Lake as disabled
        tlrs: [
          {
            name: `${descName} Open TLRs(TLR,Hot Tap,Stopple)`,
            value: parseInt(item.OpenTLR) || 'N/A'
          },
          {
            name: `${descName} TLRs Beyond Due Date`,
            value: parseInt(item.BeyondDueDate) || 'N/A',
            description: '(TLRs passed their permanent repair date)'
          },
          {
            name: `${descName} Orphan TLRs`,
            value: parseInt(item.OrphanTLRs) || 'N/A',
            description: '(TLRs with a Permanent Repair Date<= 365 Days and No Permanent Repair Work Order Determined.)'
          }
        ],
        details: [
          { title: `${descName} open TLRs(TLR,Hot,Tap,Stopple)`, count: parseInt(item.OpenTLR) || 'N/A' },
          { title: 'Beyond Due Date', count: parseInt(item.BeyondDueDate) || 'N/A' },
          { title: 'Orphan TLRs', count: parseInt(item.OrphanTLRs) || 'N/A' },
        ]
      };
    });
  }

  /**
   * Creates static location data when API fails
   */
  private createStaticLocationData() {
    this.locations = this.facilityCodes.map(code => {
      const locationName = this.nameMap[code] || code;

      // Use different image for each location based on facility code
      const imagePath = this.getLocationImagePath(code);

      return {
        name: locationName,
        route: '/details',
        image: imagePath, // Dynamic image path based on location
        disabled: code === 'SL', // Mark Salt Lake as disabled
        tlrs: [
          {
            name: `${code} Open TLRs(TLR,Hot,Tap,Stopple)`,
            value: 0 // Default value when API fails
          },
          {
            name: `${code} Open TLRs Beyond Due Date`,
            value: 0, // Default value when API fails
            description: '(TLRs passed their permanent repair date)'
          },
          {
            name: `${code} Orphan TLRs`,
            value: 0, // Default value when API fails
            description: '(TLRs with a Permanent Repair Date<= 365 Days and No Permanent Repair Work Order Determined.)'
          }
        ],
        details: [
          { title: `${code} open TLRs(TLR,Hot,Tap,Stopple)`, count: 0 },
          { title: 'Beyond Due Date', count: 0 },
          { title: 'Orphan TLRs', count: 0 },
        ]
      };
    });
  }

  /**
   * Returns the appropriate image path for a location based on its facility code
   */
  private getLocationImagePath(facilityCode: string): string {
    // Map facility codes to their respective image paths
    switch (facilityCode) {
      case 'ES':
        return 'assets/TLR_Location_Images/Refinery_ES.png';
      case 'PA':
        return 'assets/TLR_Location_Images/Refinery_PA.png';
      case 'PN':
        return 'assets/TLR_Location_Images/Refinery_PN.jpg'; // Note: PN uses jpg
      case 'SL':
        return 'assets/TLR_Location_Images/Refinery_SL.png';
      default:
        return 'assets/location.PNG'; // Fallback to default image
    }
  }

  /**
   * Gets the facility code from a location name
   */
  getFacilityCode(locationName: string): string {
    if (locationName === 'EI Segundo') {
      return 'ES';
    } else if (locationName === 'Pascagoula') {
      return 'PA';
    } else if (locationName === 'Pasadena') {
      return 'PN';
    } else if (locationName === 'Salt Lake') {
      return 'SL';
    }
    return '';
  }
  navigate(location: any) {
    if (location.disabled) {
      return; // Don't navigate if location is disabled
    }

    // Use the helper method to get facility code
    this.facilityCode = this.getFacilityCode(location.name);

    // Store facility code in localStorage
    localStorage.setItem('current_facility_id', this.facilityCode);
    this.commonService.updateCurrentFacilityId(this.facilityCode);


    // Navigate using router
    this.router.navigate([location.route]);
  }
}
